import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { vuser } from '../../services/api';
import type { ApiResponse } from '../../types/api';
import type { RagDocument } from '../../types/rag';
import MultiSelect, { type MultiSelectOption } from '../admin/MultiSelect';
import { getErrorMessage } from '../../utils/errorUtils';
import './RagDocIdsField.css';

function extractRagDocumentList(resp: unknown): RagDocument[] {
  const r = resp as ApiResponse<RagDocument[]>;
  if (r?.success === false) {
    throw new Error(r.error?.message || 'Request failed');
  }
  return Array.isArray(r?.data) ? r.data : [];
}

export type RagDocIdsFieldProps = {
  integrationId?: string | null;
  useRag: boolean;
  ragDocIds?: string[];
  onChange: (next: string[] | undefined) => void;
  disabled?: boolean;
  /** Unique suffix for radio `name` / labels (e.g. coordinator or worker id). */
  idSuffix: string;
};

/**
 * When RAG is enabled: choose all indexed docs (default, no `rag_doc_ids` in JSON)
 * or restrict to selected integration RAG document ids.
 */
export function RagDocIdsField({
  integrationId,
  useRag,
  ragDocIds,
  onChange,
  disabled = false,
  idSuffix,
}: RagDocIdsFieldProps) {
  const [docs, setDocs] = useState<RagDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const restrict = Boolean(ragDocIds && ragDocIds.length > 0);

  const loadDocs = useCallback(async () => {
    if (!integrationId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await vuser.ragListDocuments(integrationId);
      setDocs(extractRagDocumentList(resp));
    } catch (e: unknown) {
      setLoadError(getErrorMessage(e, 'Failed to load RAG documents'));
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    if (!useRag || !integrationId) {
      setDocs([]);
      setLoadError(null);
      return;
    }
    void loadDocs();
  }, [useRag, integrationId, loadDocs]);

  const selectableDocIds = useMemo(
    () => new Set(docs.filter((d) => d.status !== 'ERROR').map((d) => d.doc_id)),
    [docs]
  );
  const multiOptions: MultiSelectOption[] = docs.map((d) => ({
    value: d.doc_id,
    label:
      d.status === 'ERROR'
        ? `${d.filename} · ERROR (not available for selection)`
        : `${d.filename}${d.status && d.status !== 'READY' ? ` · ${d.status}` : ''}`,
    disabled: d.status === 'ERROR',
  }));

  const selectedIds = (ragDocIds ?? []).filter((id) => selectableDocIds.has(id));

  const applyAllReadyIds = (list: RagDocument[]) => {
    const ready = list.filter((d) => d.status === 'READY').map((d) => d.doc_id);
    const ids =
      ready.length > 0
        ? ready
        : list
            .filter((d) => d.status !== 'ERROR')
            .map((d) => d.doc_id)
            .filter(Boolean);
    onChange(ids.length ? ids : undefined);
  };

  useEffect(() => {
    if (!useRag || !integrationId) return;
    const current = ragDocIds ?? [];
    if (current.length === 0) return;
    const cleaned = current.filter((id) => selectableDocIds.has(id));
    if (cleaned.length !== current.length) {
      onChange(cleaned.length ? cleaned : undefined);
    }
  }, [useRag, integrationId, ragDocIds, selectableDocIds, onChange]);

  if (!useRag) return null;

  const radioName = `rag-doc-scope-${idSuffix}`;

  return (
    <div className="rag-doc-ids-field">
      <div className="rag-doc-ids-field__title">RAG documents</div>
      {!integrationId ? (
        <p className="rag-doc-ids-field__hint rag-doc-ids-field__hint--standalone">
          Select an integration to load document ids for RAG scope.
        </p>
      ) : (
        <>
          <p className="rag-doc-ids-field__picker-caption" id={`rag-doc-scope-caption-${idSuffix}`}>
            Scope (choose one) — which documents retrieval may use.
          </p>
          <div
            className="rag-doc-scope-picker"
            role="radiogroup"
            aria-labelledby={`rag-doc-scope-caption-${idSuffix}`}
          >
            <label
              className={`rag-doc-scope-picker__segment${!restrict ? ' rag-doc-scope-picker__segment--active' : ''}${
                disabled ? ' rag-doc-scope-picker__segment--disabled' : ''
              }`}
            >
              <input
                type="radio"
                className="rag-doc-scope-picker__input"
                name={radioName}
                checked={!restrict}
                disabled={disabled}
                onChange={() => onChange(undefined)}
              />
              <span className="rag-doc-scope-picker__label">All documents</span>
            </label>
            <label
              className={`rag-doc-scope-picker__segment${restrict ? ' rag-doc-scope-picker__segment--active' : ''}${
                disabled ? ' rag-doc-scope-picker__segment--disabled' : ''
              }`}
            >
              <input
                type="radio"
                className="rag-doc-scope-picker__input"
                name={radioName}
                checked={restrict}
                disabled={disabled}
                onChange={() => {
                  void (async () => {
                    let list = docs;
                    if (!integrationId) return;
                    if (list.length === 0) {
                      setLoading(true);
                      setLoadError(null);
                      try {
                        const resp = await vuser.ragListDocuments(integrationId);
                        list = extractRagDocumentList(resp);
                        setDocs(list);
                      } catch (e: unknown) {
                        setLoadError(getErrorMessage(e, 'Failed to load RAG documents'));
                        setLoading(false);
                        return;
                      } finally {
                        setLoading(false);
                      }
                    }
                    applyAllReadyIds(list);
                  })();
                }}
              />
              <span className="rag-doc-scope-picker__label">Only selected</span>
            </label>
          </div>
          {restrict ? (
            <div style={{ marginTop: 12 }}>
              {loadError ? (
                <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#B85400' }}>{loadError}</p>
              ) : null}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <button
                  type="button"
                  className="ac-btn"
                  disabled={disabled || loading || !integrationId}
                  onClick={() => void loadDocs()}
                  style={{ fontSize: 12, padding: '4px 10px' }}
                >
                  {loading ? 'Loading…' : 'Reload list'}
                </button>
                <button
                  type="button"
                  className="ac-btn ac--primary"
                  disabled={disabled || loading || docs.length === 0}
                  onClick={() => applyAllReadyIds(docs)}
                  style={{ fontSize: 12, padding: '4px 10px' }}
                >
                  Use all listed IDs
                </button>
              </div>
              <p className="rag-doc-ids-field__list-caption">Click a row to include or exclude a document.</p>
              <p className="rag-doc-ids-field__hint rag-doc-ids-field__hint--below-multiselect">
                Files with status `ERROR` are shown for visibility, but cannot be selected.
              </p>
              <MultiSelect
                className="rag-doc-multi-select"
                listLayout="inline"
                showInput={false}
                options={multiOptions}
                value={selectedIds}
                onChange={(ids) => onChange(ids.length ? ids : undefined)}
                placeholder={loading ? 'Loading documents…' : 'No documents selected — use the list below.'}
                disabled={disabled || loading}
              />
              <p className="rag-doc-ids-field__hint rag-doc-ids-field__hint--below-multiselect">
                Retrieval is limited to the selected files. Switch scope to “All documents” to use the full RAG index
                for this integration.
              </p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
